import React from "react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import Milestone from "./Milestone";
import { formatMoney } from "../pricing";
import {
  TRIAL_DAYS,
  TRIAL_REMINDER_DAYS,
  trialReminderOf,
} from "../trialState";
import type { PriceBreakdown, SubscriptionPlan } from "../types/billing.types";

/** One line of the breakdown: what it is on the left, what it comes to on the
 *  right. Tabular figures so the column of rupees lines up. */
const Line: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</span>
    <span className="tabular-nums text-[13px] 3xl:text-[14px] font-medium text-ink">
      {value}
    </span>
  </div>
);

interface TrialSummaryProps {
  plan: SubscriptionPlan;
  /**
   * The quote for the plan behind the trial.
   *
   * Only the tax *rate* is read off it, for the label on a line whose figure is
   * zero — see below. The plan's own price is deliberately not drawn: nothing
   * is being charged, now or at the end, and a figure on this card would be a
   * number nobody has agreed to pay.
   */
  price: PriceBreakdown;
  /** The last day the fourteen cover — day fourteen, today being day one.
   *  Passed in rather than worked out here so the summary and the confirmation
   *  quote one date; see `trialState`. */
  endsOn: Date;
  onStart: () => void;
  /** Back to the offer, to start differently. Left off where there is nowhere
   *  to go back to. */
  onEdit?: () => void;
  /** The details are with the server — the button spins and stays pressed. */
  starting?: boolean;
  /**
   * Whether the card carries the button that starts the trial.
   *
   * False on a phone, where the card is the first thing on the page and the
   * button is the last — see `TrialDetailsPage`. The line under the button
   * goes with it: it is the reassurance that makes the press easy, so it
   * belongs wherever the press is.
   */
  payable?: boolean;
}

/**
 * What starting the trial signs somebody up to, beside the form that says who
 * it would be billed to.
 *
 * ## Every figure on it is zero, and that is the offer
 *
 * The card used to print what the plan would cost when the fortnight ended, on
 * the argument that a charge nobody was warned about is how a free trial turns
 * into a complaint. That warning is no longer the true one: the trial takes no
 * payment detail and converts into nothing — it ends, Pro pauses, and the
 * account stays on Free with its data intact until somebody chooses to
 * subscribe. There is no charge to warn about, so quoting one would be
 * inventing an obligation.
 *
 * What is left is three zeroes and the timeline, which is the whole of what
 * this step commits anybody to. The GST line keeps its rate in the label
 * because the rate is a fact about the plan; the figure beside it is ₹0 for
 * the same reason every other figure is.
 *
 * ## And no coupon line
 *
 * It had one, carrying the code on the order where the paid card carries the
 * discount. It went with the field that fed it: the trial converts into
 * nothing and `POST /billing/trial/` takes no body, so a code put on this step
 * was never going anywhere. See `TrialDetailsPage`. The paid summary keeps its
 * line, where there is a charge for a coupon to come off.
 *
 * The dates are the only arithmetic here, and they have nowhere else to come
 * from — there is no trial endpoint to date them; see `trialState`.
 */
const TrialSummary: React.FC<TrialSummaryProps> = ({
  plan,
  price,
  endsOn,
  onStart,
  onEdit,
  starting = false,
  payable = true,
}) => {
  // The reminder falls three days before the last day the fortnight covers.
  // Day one is today — the way a recharge counts validity — so `endsOn` is the
  // fourteenth day and this is the eleventh; see `trialState`.
  const remindsOn = trialReminderOf(endsOn);

  return (
    <section
      aria-labelledby="trial-summary-heading"
      className="card-surface border border-[#5BD3E1] bg-[#F8FEFF] p-5 3xl:p-6"
    >
      <h2
        id="trial-summary-heading"
        className="text-[14px] 3xl:text-[15px] font-medium uppercase tracking-[0.08em] text-ink"
      >
        Plan details
      </h2>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[12px] 3xl:text-[13px] text-ink-muted">
            {plan.productName}
          </p>
          <p className="mt-0.5 flex flex-wrap items-baseline gap-2 text-[15px] 3xl:text-[16px] font-semibold text-ink">
            {/* What is being started, which is the trial — not a term. The
                plan behind it is named above and costs nothing until somebody
                subscribes. */}
            Free trial
            {onEdit && (
              // Starting differently is a different answer to the question the
              // offer asked, so it goes back to that list rather than opening a
              // second picker here.
              <button
                type="button"
                onClick={onEdit}
                disabled={starting}
                className="text-[13px] 3xl:text-[14px] font-medium text-[#00BACB] underline-offset-2 transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Edit
              </button>
            )}
          </p>
        </div>
        <span className="tabular-nums text-[15px] 3xl:text-[16px] font-medium text-ink">
          {formatMoney(0)}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-2.5">
        {/* The rate is a fact about the plan and stays in the label; the figure
            is zero because nothing is being taxed. */}
        <Line
          label={`GST ${Math.round(price.taxRate * 100)}%`}
          value={formatMoney(0)}
        />
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[14px] 3xl:text-[15px] font-medium text-ink">
            Total amount
          </span>
          <span className="tabular-nums text-[14px] 3xl:text-[15px] font-semibold text-ink">
            {formatMoney(0)}
          </span>
        </div>
      </div>

      {/* The one figure that is about today, and it is nothing. It sits where
          the checkout puts its total, in the same weight, because it is the
          same line of the same question — "what am I paying now" — and the
          answer being ₹0 is the whole offer. */}
      <div className="mt-4 flex items-start justify-between gap-4 border-t border-line pt-4">
        <div>
          <p className="text-[16px] 3xl:text-[18px] font-semibold text-ink">
            Payable amount
          </p>
          <p className="mt-0.5 text-[12px] 3xl:text-[13px] text-ink-muted">
            {TRIAL_DAYS}-day free trial &middot; No payment required
          </p>
        </div>
        <span className="tabular-nums text-[21px] 3xl:text-[24px] font-bold text-ink">
          {formatMoney(0)}
        </span>
      </div>

      {/* Three stops, and none of them is a charge. The middle is a promise
          about a mail, and it is here because a trial that ends without warning
          is the complaint every free trial gets. The last says what actually
          happens on the day — which is nothing anybody has to act on, and
          saying so is what makes the first press easy. */}
      <ul className="mt-5">
        <Milestone
          done
          title="Free trial starts from today"
          detail={`Full access to ${plan.productName}`}
          // What pressing the button gets you, said as a state rather than as
          // an event — violet, the colour this feature gives an aside beside a
          // figure.
          tag={
            <span className="rounded-full border border-[#DCCFFA] bg-[#F4EEFF] px-2.5 py-0.5 text-[11px] 3xl:text-[12px] font-medium text-[#6234CC]">
              {plan.productName} unlocked
            </span>
          }
        />
        <Milestone
          title={`${formatCreatedOn(remindsOn)} · trial ending reminder`}
          detail={`We'll email you ${TRIAL_REMINDER_DAYS} days before it ends.`}
        />
        <Milestone
          last
          title={`${formatCreatedOn(endsOn)} · trial ends`}
          detail="Pro pauses unless you subscribe. Your data stays safe."
        />
      </ul>

      {payable && (
        <>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            className="mt-5"
            loading={starting}
            onClick={onStart}
          >
            Activate free trial
          </Button>

          {/* Under the button rather than on it: it is the reassurance that
              makes the press easy, and a button that carried it would be a
              button with a sentence on it.

              It used to go on to say "Start free, pay after 14 days", which
              is a promise of a charge this trial does not make — see the
              note at the head of this card. */}
          <p className="mt-3 text-center text-[12px] 3xl:text-[13px] text-ink-subtle">
            No payment detail required
          </p>
        </>
      )}
    </section>
  );
};

export default TrialSummary;
